// Kepabeanan › Dashboard, "Operational Dashboard" (revamp PR 5, unit F): the chart pilot. Same requests, gates, tabs,
// date defaults and copy as the Mantine page it replaces (d0-inventory §2.1); the charts live in two lazy components
// so Recharts stays out of the main chunk (App.tsx imports this page eagerly).
//
// Kept as they were: the three hooks in their order, including the usage-dashboard call that renders nothing but
// still gates the page's loading and error states; the PPJK state that starts '' and takes localStorage nomor_ppjk
// in an effect; the admin picker (super admins + samuel); the default dates −6 / +1 days sent as yyyy-MM-dd; the
// demo email's canned data (inside the hooks); the `dashboard-stats` tour anchor.
// Changed by decision #15: the first load shows an in-place PageState under the header (the anchor is already on
// the page, so the welcome tour keeps its stats step); a date or PPJK change keeps the page and refetches inline.
import { BarChart3, LineChart } from 'lucide-react';
import { lazy, type ReactNode, Suspense, useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';

import { PageState, useLastData } from '@/components/charts';
import { DateField } from '@/components/form/date-field';
import { Breadcrumb, BreadcrumbItem, BreadcrumbList } from '@/components/lumen/breadcrumb';
import { Combobox } from '@/components/lumen/combobox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/lumen/tabs';
import { PageHeader, PageShell } from '@/components/shell/PageHeader';
import { useLanguage } from '@/contexts/LanguageContext';
import usePpjkOptions from '@/features/ppjk-picker/usePpjkOptions';
import { formatTemplate } from '@/lib/format-template';
import { isSuperAdminEmail } from '@utils/access';
import useGetUsageDashboard from './apis/use-get-usage-dashboard';
import useGetUsageDashboardGraph from './apis/use-get-usage-dashboard-graph';
import useGetUserStats from './apis/use-get-user-stats';

// The chart components carry Recharts; load them on demand.
const UsageDashboardGraph = lazy(() => import('./components/UsageDashboardGraph'));
const UserStatsGraph = lazy(() => import('./components/UserStatsGraph'));

// The usage call keeps the first page of the table it once fed (never rendered): offset 0, 10 rows, newest first.
const USAGE_PAGE = { offset: 0, limit: 10, orderBy: 'tanggalDibuat', ascending: false } as const;

export default function DashboardPage() {
  const { t } = useLanguage();
  const [startDate, setStartDate] = useState<Date | null>(() => {
    const date = new Date();
    date.setDate(date.getDate() - 6);
    return date;
  });
  const [endDate, setEndDate] = useState<Date | null>(() => {
    const date = new Date();
    date.setDate(date.getDate() + 1);
    return date;
  });

  const [activeTab, setActiveTab] = useState<string>('graph');

  // PPJK selection state
  const [selectedPpjk, setSelectedPpjk] = useState<string>('');

  // Check if user is admin and get default PPJK
  const isAdmin = useMemo(() => {
    if (typeof window !== 'undefined') {
      const authData = localStorage.getItem('authData');
      if (authData) {
        try {
          const parsed = JSON.parse(authData);
          const email = parsed.email;
          return isSuperAdminEmail(email) || email === 'samuel.pamudji@jar-vis.com';
        } catch {
          return false;
        }
      }
    }
    return false;
  }, []);

  // The picker works in NPWPs: it opens on the caller's own PPJK and sends
  // the picked one as `ppjkNpwp`. The server honours that for super admins
  // only and resolves the name itself; everyone else always gets their own.
  const defaultPpjk = useMemo(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('nomor_ppjk') || '';
    }
    return '';
  }, []);

  // Set default PPJK on mount
  useEffect(() => {
    setSelectedPpjk(defaultPpjk);
  }, [defaultPpjk]);

  const { options: ppjkOptions } = usePpjkOptions(defaultPpjk, isAdmin);

  const dashboardParams = useMemo(
    () => ({
      startDate: startDate ? format(startDate, 'yyyy-MM-dd') : undefined,
      endDate: endDate ? format(endDate, 'yyyy-MM-dd') : undefined,
      ...USAGE_PAGE,
      ppjkNpwp: selectedPpjk || undefined,
    }),
    [startDate, endDate, selectedPpjk]
  );

  const { loading: usageLoading, error: usageError } = useGetUsageDashboard(dashboardParams);

  // Graph data (without pagination)
  const graphParams = useMemo(
    () => ({
      startDate: startDate ? format(startDate, 'yyyy-MM-dd') : undefined,
      endDate: endDate ? format(endDate, 'yyyy-MM-dd') : undefined,
      ppjkNpwp: selectedPpjk || undefined,
    }),
    [startDate, endDate, selectedPpjk]
  );

  const { data: graphData, loading: graphLoading, error: graphError } = useGetUsageDashboardGraph(graphParams);

  // User Stats data (without pagination for graph view)
  const userStatsParams = useMemo(
    () => ({
      startDate: startDate ? format(startDate, 'yyyy-MM-dd') : undefined,
      endDate: endDate ? format(endDate, 'yyyy-MM-dd') : undefined,
      offset: 0,
      limit: 100, // Get top 100 users for comprehensive stats
      orderBy: 'totalAju',
      ascending: false,
      ppjkNpwp: selectedPpjk || undefined,
    }),
    [startDate, endDate, selectedPpjk]
  );

  const { data: userStatsData, loading: userStatsLoading, error: userStatsError } = useGetUserStats(userStatsParams);

  // A new date or PPJK is a new query key, whose data is undefined until it loads: hold the last rows so the
  // charts stay on screen, dimmed, while it does.
  const shownGraph = useLastData(graphData, !graphLoading && !graphError);
  const shownUserStats = useLastData(userStatsData, !userStatsLoading && !userStatsError);

  const handlePpjkChange = (value: string | null) => {
    setSelectedPpjk(value || defaultPpjk);
  };

  // The old page's gates: both page calls loading → the page loader; both failed → the page error.
  const pageLoading = usageLoading && graphLoading;
  const pageError = usageError && graphError;
  // Only the first load shows the loader; later loads refetch inline (decision #15).
  const [loadedOnce, setLoadedOnce] = useState(false);
  if (!pageLoading && !loadedOnce) setLoadedOnce(true);
  const firstLoad = pageLoading && !loadedOnce;

  let body: ReactNode;
  if (firstLoad) {
    body = <PageState kind="loading" title={t('lumen.dashboard.loading')} className="min-h-96" />;
  } else if (pageError && !pageLoading) {
    body = (
      <PageState
        kind="error"
        title={formatTemplate(t('lumen.dashboard.error'), { error: usageError || graphError })}
        className="min-h-96"
      />
    );
  } else {
    body = (
      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value || 'graph')} className="gap-4">
        <TabsList variant="line">
          <TabsTrigger value="graph">
            <LineChart aria-hidden />
            {t('lumen.dashboard.graphView')}
          </TabsTrigger>
          <TabsTrigger value="userstats">
            <BarChart3 aria-hidden />
            {t('lumen.dashboard.userStats')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="graph">
          <Suspense fallback={<PageState kind="loading" title={t('lumen.dashboard.loadingCharts')} />}>
            <UsageDashboardGraph data={shownGraph} loading={graphLoading} error={graphError} />
          </Suspense>
        </TabsContent>

        <TabsContent value="userstats">
          <Suspense fallback={<PageState kind="loading" title={t('lumen.dashboard.loadingStats')} />}>
            <UserStatsGraph data={shownUserStats} loading={userStatsLoading} error={userStatsError} />
          </Suspense>
        </TabsContent>
      </Tabs>
    );
  }

  return (
    <div data-testid="dashboard-page">
      <PageShell>
        <PageHeader
          breadcrumbs={
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>{t('menu.kepabeanan')}</BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          }
          title={t('lumen.dashboard.title')}
          description={t('lumen.dashboard.subtitle')}
          actions={
            <>
              {isAdmin && (
                <Combobox
                  options={ppjkOptions}
                  value={selectedPpjk}
                  // Mantine's allowDeselect={false}: picking the selected PPJK again changes nothing.
                  onChange={(value) => {
                    if (value !== null) handlePpjkChange(value);
                  }}
                  placeholder={t('lumen.dashboard.selectPpjk')}
                  aria-label={t('lumen.dashboard.selectPpjk')}
                  emptyText={t('lumen.dashboard.ppjkNotFound')}
                  className="w-80"
                />
              )}
              <DateField
                value={startDate}
                onChange={setStartDate}
                placeholder={t('lumen.dashboard.pickStartDate')}
                className="w-40"
              />
              <DateField
                value={endDate}
                onChange={setEndDate}
                placeholder={t('lumen.dashboard.pickEndDate')}
                className="w-40"
              />
            </>
          }
        />

        {/* The welcome tour's `stats` step anchors here; it is on the page from the first render. */}
        <div data-tour="dashboard-stats">{body}</div>
      </PageShell>
    </div>
  );
}
